import { useState, useEffect, useRef } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Check, X, Loader2, AlertTriangle } from 'lucide-react'
import { analyzeFlashCrop, generatePaddingOverlay, cropImageToDataUrl, type FlashCropAnalysis } from '@/lib/flashCropValidation'
import { uploadFlashFromDataUrl } from '@/lib/flashStorage'

/**
 * Generate user-friendly feedback message for padding issues
 */
function generatePaddingFeedback(analysis: FlashCropAnalysis): string {
  const insufficientPaddingSides: string[] = []
  
  // Check each side to see if it needs more padding
  if (analysis.padding.top.needsMore) {
    insufficientPaddingSides.push('top')
  }
  
  if (analysis.padding.right.needsMore) {
    insufficientPaddingSides.push('right')
  }
  
  if (analysis.padding.bottom.needsMore) {
    insufficientPaddingSides.push('bottom')
  }
  
  if (analysis.padding.left.needsMore) {
    insufficientPaddingSides.push('left')
  }
  
  // If all sides are insufficient, return a generic message
  if (insufficientPaddingSides.length === 0) {
    return 'Image has sufficient padding and will be automatically cropped to optimal dimensions'
  }
  
  // Generate message for sides that need more padding
  const sidesText = insufficientPaddingSides.length === 1 
    ? `${insufficientPaddingSides[0]} side`
    : insufficientPaddingSides.length === 2
    ? `${insufficientPaddingSides.join(' and ')} sides`
    : insufficientPaddingSides.length === 3
    ? `${insufficientPaddingSides.slice(0, -1).join(', ')}, and ${insufficientPaddingSides[insufficientPaddingSides.length - 1]} sides`
    : 'all sides'
  
  const minPaddingPercent = 12
  return `Need at least ${minPaddingPercent}% padding on the ${sidesText}. Please add more space around your design.`
}

interface FlashCropValidatorProps {
  imageUrl: string
  onValidationComplete: (isValid: boolean, analysis?: FlashCropAnalysis, croppedImageUrl?: string) => void
  className?: string
  autoAnalyze?: boolean
  onRemove?: () => void
  displayName: string
  flashId: string
}

export function FlashCropValidator({ 
  imageUrl, 
  onValidationComplete, 
  className = '',
  autoAnalyze = false,
  onRemove,
  displayName,
  flashId
}: FlashCropValidatorProps) {
  const [analysis, setAnalysis] = useState<FlashCropAnalysis | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentImageUrl, setCurrentImageUrl] = useState(imageUrl)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  const analyzeImage = async () => {
    setLoading(true)
    setError(null)
    setAnalysis(null)

    try {
      const result = await analyzeFlashCrop(currentImageUrl)
      
      let finalImageUrl = currentImageUrl
      let finalAnalysis = result
      
      // If valid and needs cropping, crop and reupload
      if (result.isValid && result.optimalCrop) {
        const { x, y, width, height, appliedPadding } = result.optimalCrop
        
        // Check if cropping is actually needed (dimensions different from original)
        const needsCropping = x > 0 || y > 0 || 
                             width < result.imageDimensions.width || 
                             height < result.imageDimensions.height
        
        if (needsCropping) {
          // Crop the image
          const croppedDataUrl = await cropImageToDataUrl(
            currentImageUrl,
            x,
            y,
            width,
            height
          )
          
          // Upload the cropped image
          finalImageUrl = await uploadFlashFromDataUrl(
            croppedDataUrl,
            displayName,
            `${flashId}_cropped`
          )
          
          // Update the current image URL to show the cropped version
          setCurrentImageUrl(finalImageUrl)
          
          // Recalculate analysis for the cropped image
          // The bounding box in the cropped image starts at the applied padding
          const croppedBoundingBox = {
            x: appliedPadding.left,
            y: appliedPadding.top,
            width: result.boundingBox.width,
            height: result.boundingBox.height
          }
          
          // Calculate new padding values for the cropped image
          const croppedPadding = {
            top: {
              value: appliedPadding.top,
              percentage: (appliedPadding.top / height) * 100,
              isValid: true,
              needsMore: false
            },
            right: {
              value: appliedPadding.right,
              percentage: (appliedPadding.right / width) * 100,
              isValid: true,
              needsMore: false
            },
            bottom: {
              value: appliedPadding.bottom,
              percentage: (appliedPadding.bottom / height) * 100,
              isValid: true,
              needsMore: false
            },
            left: {
              value: appliedPadding.left,
              percentage: (appliedPadding.left / width) * 100,
              isValid: true,
              needsMore: false
            }
          }
          
          // Update analysis with cropped image dimensions and adjusted bounding box
          finalAnalysis = {
            ...result,
            boundingBox: croppedBoundingBox,
            imageDimensions: { width, height },
            padding: croppedPadding,
            optimalCrop: undefined // No more cropping needed
          }
        }
      }
      
      setAnalysis(finalAnalysis)
      onValidationComplete(finalAnalysis.isValid, finalAnalysis, finalImageUrl !== currentImageUrl ? finalImageUrl : undefined)
      
      // Draw overlay after analysis
      setTimeout(() => drawPaddingOverlay(finalAnalysis), 100)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to analyze flash crop'
      setError(errorMessage)
      onValidationComplete(false)
    } finally {
      setLoading(false)
    }
  }

  const drawPaddingOverlay = (analysisData: FlashCropAnalysis) => {
    const canvas = canvasRef.current
    const image = imageRef.current
    if (!canvas || !image || !analysisData) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Set canvas size to match image display size
    const rect = image.getBoundingClientRect()
    canvas.width = rect.width
    canvas.height = rect.height

    // Calculate scale factors
    const scaleX = rect.width / analysisData.imageDimensions.width
    const scaleY = rect.height / analysisData.imageDimensions.height

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // Generate padding overlay trapezoids
    const overlays = generatePaddingOverlay(analysisData)

    // Draw each padding area as trapezoids
    Object.entries(overlays).forEach(([, overlay]) => {
      // Set color based on validation
      ctx.fillStyle = overlay.isValid ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.5)'
      ctx.strokeStyle = overlay.isValid ? 'rgba(34, 197, 94, 0.8)' : 'rgba(239, 68, 68, 0.8)'
      ctx.lineWidth = 2

      // Draw trapezoid path
      ctx.beginPath()
      
      // Scale and move to first point
      const firstPoint = overlay.points[0]
      ctx.moveTo(firstPoint.x * scaleX, firstPoint.y * scaleY)
      
      // Draw lines to all other points
      for (let i = 1; i < overlay.points.length; i++) {
        const point = overlay.points[i]
        ctx.lineTo(point.x * scaleX, point.y * scaleY)
      }
      
      // Close the path
      ctx.closePath()
      
      // Fill and stroke the trapezoid
      ctx.fill()
      ctx.stroke()
    })
  }

  // Redraw overlay when image loads or window resizes
  useEffect(() => {
    if (analysis) {
      const handleResize = () => drawPaddingOverlay(analysis)
      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }
  }, [analysis])

  return (
    <div className={`relative ${className}`}>
      {/* Image container */}
      <div className="relative inline-block">
        <img
          ref={imageRef}
          src={currentImageUrl}
          alt="Flash design"
          className="w-full h-auto object-contain rounded-lg"
          onLoad={() => {
            if (analysis) {
              drawPaddingOverlay(analysis)
            } else if (autoAnalyze && !loading && !error) {
              // Auto-analyze when image loads
              analyzeImage()
            }
          }}
        />
        
        {/* Canvas overlay for padding visualization */}
        <canvas
          ref={canvasRef}
          className="absolute top-0 left-0 pointer-events-none rounded-lg"
          style={{ width: '100%', height: '100%' }}
        />

        {/* Validation status indicator */}
        <div className="absolute top-2 right-2">
          {loading && (
            <Badge variant="secondary" className="bg-blue-500 text-white">
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              {autoAnalyze ? 'Checking padding...' : 'Analyzing...'}
            </Badge>
          )}
          {error && (
            <Badge variant="destructive">
              <AlertTriangle className="w-3 h-3 mr-1" />
              Error
            </Badge>
          )}
          {analysis && !loading && (
            <>
              {analysis.isValid ? (
                <Badge 
                  variant="secondary"
                  className="bg-green-500 text-white"
                >
                  <Check className="w-3 h-3 mr-1" />
                  Valid Crop
                </Badge>
              ) : (
                <Badge 
                  variant="secondary"
                  className={`bg-red-500 text-white ${onRemove ? 'cursor-pointer hover:bg-red-600' : ''}`}
                  onClick={onRemove}
                >
                  <X className="w-3 h-3 mr-1" />
                  Remove
                </Badge>
              )}
            </>
          )}
        </div>
      </div>

      {/* Analysis button */}
      {!analysis && !loading && !error && (
        <div className="mt-3 text-center">
          <Button
            onClick={analyzeImage}
            size="sm"
            variant="outline"
            className="flex items-center gap-2"
          >
            <AlertTriangle className="w-4 h-4" />
            Check Crop Quality
          </Button>
        </div>
      )}

      {/* Retry button for errors */}
      {error && (
        <div className="mt-3 text-center">
          <p className="text-sm text-destructive mb-2">{error}</p>
          <Button
            onClick={analyzeImage}
            size="sm"
            variant="outline"
            className="flex items-center gap-2"
          >
            Retry Analysis
          </Button>
        </div>
      )}

      {/* Analysis details */}
      {analysis && (
        <div className="mt-3 space-y-2">
          {/* Padding details */}
          <div className="grid grid-cols-2 gap-1 text-xs">
            {Object.entries(analysis.padding).map(([side, padding]) => (
              <div 
                key={side}
                className={`flex items-center gap-1 ${
                  padding.isValid ? 'text-green-700' : 'text-red-700'
                }`}
              >
                {padding.isValid ? (
                  <Check className="w-3 h-3" />
                ) : (
                  <X className="w-3 h-3" />
                )}
                <span className="capitalize">{side}: {Math.round(padding.percentage)}%</span>
              </div>
            ))}
          </div>

          {!analysis.isValid && (
            <div className="text-xs text-red-700 bg-red-50 p-2 rounded">
              <strong>Crop Issue:</strong> {generatePaddingFeedback(analysis)}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
